/**
 * SEO configuration and structured data for traditional and agentic discoverability.
 * Excludes locked projects and sensitive routes from indexing.
 */

import { PROFILE_TEXT } from '../content/profileCopy';

export const SITE_URL = 'https://bes.art';

/** Routes that should NOT be indexed (locked projects, sensitive content) */
export const NOINDEX_ROUTES = [
  '/volvo-configurator',
  '/volvo-rider',
  '/resume',
] as const;

/** Public routes to include in sitemap (excludes locked, resume, alternate views) */
export const PUBLIC_SITEMAP_ROUTES: { path: string; priority: number; changefreq?: string }[] = [
  { path: '/', priority: 1.0, changefreq: 'monthly' },
  { path: '/profile', priority: 0.9, changefreq: 'monthly' },
  { path: '/mack-trucks', priority: 0.8, changefreq: 'monthly' },
  { path: '/volvo-trucks-na', priority: 0.8, changefreq: 'monthly' },
  { path: '/zeekr', priority: 0.8, changefreq: 'monthly' },
  { path: '/polestar-engineered', priority: 0.8, changefreq: 'monthly' },
  { path: '/volvo-trucks-ar', priority: 0.8, changefreq: 'monthly' },
  { path: '/formula1', priority: 0.8, changefreq: 'monthly' },
  { path: '/drita-ime', priority: 0.8, changefreq: 'monthly' },
  { path: '/ai-playground', priority: 0.8, changefreq: 'monthly' },
  { path: '/word-play', priority: 0.8, changefreq: 'monthly' },
  { path: '/nivea', priority: 0.8, changefreq: 'monthly' },
  { path: '/nike', priority: 0.8, changefreq: 'monthly' },
  { path: '/nissan-idx', priority: 0.8, changefreq: 'monthly' },
  { path: '/volvo-trucks-aero', priority: 0.8, changefreq: 'monthly' },
  { path: '/dieter-rams', priority: 0.8, changefreq: 'monthly' },
];

export function isNoindexRoute(pathname: string): boolean {
  const path = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;
  return NOINDEX_ROUTES.some((route) => path === route || path.startsWith(route + '/'));
}

/** Person schema for homepage/profile - helps AI agents understand who you are */
export function getPersonSchema(imageUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Bes Bujupi',
    alternateName: 'Besart Bujupi',
    jobTitle: PROFILE_TEXT.seo.jobTitle,
    description: PROFILE_TEXT.seo.personDescription,
    url: SITE_URL,
    sameAs: [
      'https://www.linkedin.com/in/besbujupi/',
      'https://www.instagram.com/besbujupi',
      'https://x.com/besbujupi',
      'https://www.behance.net/besbujupi',
    ],
    knowsAbout: [
      'Brand Building',
      'Product Design',
      '3D Visualization',
      'AR/VR',
      'Design Systems',
      'Creative Leadership',
    ],
    image: imageUrl,
  };
}

/** Service schema - helps agents match you to "creative director for X" queries */
export function getServiceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Creative Direction & Design Leadership',
    description: PROFILE_TEXT.seo.serviceDescription,
    provider: {
      '@type': 'Person',
      name: 'Bes Bujupi',
      url: SITE_URL,
    },
    areaServed: 'Worldwide',
    serviceType: ['Creative Direction', 'Brand Building', 'Product Design', '3D/AR Experiences', 'Design Systems'],
  };
}

/** WebSite schema with ContactAction - tells agents how to direct users */
export function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: PROFILE_TEXT.seo.websiteName,
    url: SITE_URL,
    description: PROFILE_TEXT.seo.websiteDescription,
    author: {
      '@type': 'Person',
      name: 'Bes Bujupi',
      url: SITE_URL,
    },
    potentialAction: {
      '@type': 'ViewAction',
      target: `${SITE_URL}/profile`,
      name: 'View profile and contact information',
    },
  };
}

/** CreativeWork schema for case study pages - only for public projects */
export function getCreativeWorkSchema(params: {
  name: string;
  description: string;
  url: string;
  imageUrl: string;
  keywords?: string[];
}) {
  const { name, description, url, imageUrl, keywords = [] } = params;
  return {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name,
    description: description.slice(0, 500),
    url,
    image: imageUrl,
    author: {
      '@type': 'Person',
      name: 'Bes Bujupi',
      url: SITE_URL,
    },
    ...(keywords.length > 0 && { keywords: keywords.join(', ') }),
  };
}
